<script setup lang="ts">
import * as turf from '@turf/turf';
import { ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type { GeocodingFeature } from '@nora-monorepo/nora-api-client';
import type { NamedLatLonCoordinate } from '@nora-monorepo/shared';

import GeoSearch from '#nora-ui/components/geo-search/GeoSearch.vue';
import type { RouteTool } from '#nora-ui/map/drawtools/route-tool';
import { RouteViewMode } from '#nora-ui/map/drawtools/stores/route-tool-store';

export interface RouteToolAddressSearchProps {
  tool: RouteTool;
  mapId: string;
}

const props = defineProps<RouteToolAddressSearchProps>();

const { tool } = toRefs(props);
const { t } = useI18n();
const geoSearchRef = ref<InstanceType<typeof GeoSearch> | null>(null);

async function onLocationSelected(feature: GeocodingFeature) {
  const centroid = turf.centroid(feature);
  const [longitude, latitude] = centroid.geometry.coordinates;
  const properties = feature.properties;
  const name = properties?.geocoding?.label;

  if (latitude === undefined || longitude === undefined || !name) {
    return;
  }

  const namedCoordinate: NamedLatLonCoordinate = {
    latitude,
    longitude,
    name,
  };

  tool.value.addPoint(namedCoordinate);
  await tool.value.handleRouteChange();

  resetSearch();
}

function onSearchQueryUpdated(searchQuery: string) {
  if (searchQuery.length > 0) {
    tool.value.routeToolStore.viewMode = RouteViewMode.Address;
  }
  else {
    tool.value.routeToolStore.viewMode = RouteViewMode.PointList;
  }
}

function resetSearch() {
  if (geoSearchRef.value) {
    geoSearchRef.value.resetSearch();
  }
}

function onResetCompleted() {
  tool.value.routeToolStore.viewMode = RouteViewMode.PointList;
}
</script>

<template>
  <div class="flex flex-col max-h-[200px] overflow-y-auto">
    <GeoSearch
      ref="geoSearchRef"
      :map-id="mapId"
      label=""
      :placeholder="t('drawTools.route.searchPlaceholder')"
      :focus-on-mount="false"
      @feature-selected="onLocationSelected"
      @reset-completed="onResetCompleted"
      @search-query-updated="onSearchQueryUpdated"
    />
  </div>
</template>
